"use client";

import React, { useEffect, useRef, useState } from "react";
import { AppBar, Toolbar, Box, Button, IconButton, Tooltip } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CardModal from "./CardModal";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";
import { logout } from "../../../util/fetchWithAuth";

const TopNavbar = ({
  toggleActive,
  leftContent = null,
  rightContent = null,
  bottomNav = null,
  stickyScroll = true,
}) => {
  const { data: user, refetch } = useFetchWithAuth("/user/me/");
  const [openCard, setOpenCard] = useState(false);
  const [cardValue, setCardValue] = useState("");
  const fontScaleKey = "operatorFontScale";
  const [fontScale, setFontScale] = useState(() => {
    const stored = Number(localStorage.getItem(fontScaleKey));
    return Number.isFinite(stored) && stored > 0 ? stored : 1;
  });
  const baseFontSizeRef = useRef(null);

  // 📌 когда user загрузится — подставляем его card_number
  useEffect(() => {
    if (user?.card_number) {
      setCardValue(user.card_number);
    }
  }, [user]);

  useEffect(() => {
    if (!stickyScroll) return;
    const handleScroll = () => {
      const navbar = document.getElementById("navbar");
      if (window.scrollY > 100) {
        navbar?.classList.add("sticky");
      } else {
        navbar?.classList.remove("sticky");
      }
    };
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, [stickyScroll]);

  useEffect(() => {
    const clamped = Math.min(1.3, Math.max(0.9, fontScale));
    if (!baseFontSizeRef.current) {
      const computedSize = Number.parseFloat(
        window.getComputedStyle(document.documentElement).fontSize || "16"
      );
      const derivedBase =
        Number.isFinite(computedSize) && clamped > 0
          ? computedSize / clamped
          : 16;
      baseFontSizeRef.current =
        Number.isFinite(derivedBase) && derivedBase > 0 ? derivedBase : 16;
    }
    document.documentElement.style.fontSize = `${
      baseFontSizeRef.current * clamped
    }px`;
    localStorage.setItem(fontScaleKey, String(clamped));
  }, [fontScale, fontScaleKey]);

  const handleFontScaleIncrease = () => {
    setFontScale((prev) => Math.min(1.3, prev + 0.05));
  };
  const handleFontScaleDecrease = () => {
    setFontScale((prev) => Math.max(0.9, prev - 0.05));
  };

  const userDataForModal = user
    ? {
        username: user.username,
        combo_days: user.combo_days,
        experience: user.experience,
      }
    : null;

  return (
    <div className="top-navbar-dark">
      <AppBar
        id="navbar"
        color="inherit"
        sx={{
          backgroundColor: "#fff",
          boxShadow: "initial",
          borderRadius: "0 0 15px 15px",
          py: { xs: "15px", sm: "3px" },
          width: "auto",
          zIndex: "489",
        }}
        className="top-navbar"
      >
        <Box className="top-navbar-content">
          <Toolbar
            sx={{
              display: { xs: "block", sm: "flex" },
              justifyContent: { xs: "center", sm: "space-between" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: "10px", sm: "5px", md: "15px" },
              }}
            >
              {leftContent}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: "8px", sm: "8px", lg: "15px" },
                mt: { xs: "10px", sm: "0px" },
              }}
            >
              {rightContent}
              <Tooltip title="Зменшити текст">
                <IconButton
                  onClick={handleFontScaleDecrease}
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    width: 40,
                    height: 40,
                    color: "var(--blackColor, #232B3E)",
                    ".dark-theme &": { color: "#fff" },
                  }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Збільшити текст">
                <IconButton
                  onClick={handleFontScaleIncrease}
                  sx={{
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    width: 40,
                    height: 40,
                    color: "var(--blackColor, #232B3E)",
                    ".dark-theme &": { color: "#fff" },
                  }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  color: "var(--blackColor, #232B3E)",
                  borderColor: "#232B3E",
                  background: "transparent",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  ml: 2,
                  textTransform: "none",
                  fontSize: 15,
                  "&:hover": {
                    borderColor: "#6366F1",
                    color: "#232B3E",
                  },
                  ".dark-theme &": {
                    color: "#fff",
                  },
                }}
                onClick={() => setOpenCard(true)}
              >
                Ваша картка
              </Button>
              <Button
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  color: "var(--blackColor, #232B3E)",
                  borderColor: "#E57373",
                  background: "transparent",
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  fontSize: 15,
                  "&:hover": {
                    borderColor: "#E53935",
                    color: "#232B3E",
                  },
                  ".dark-theme &": {
                    color: "#fff",
                  },
                }}
                onClick={logout}
              >
                Вийти
              </Button>
            </Box>
          </Toolbar>
        </Box>

        {bottomNav}
      </AppBar>

      {userDataForModal && (
        <CardModal
          open={openCard}
          onClose={() => setOpenCard(false)}
          cardValue={cardValue}
          setCardValue={setCardValue}
          userData={userDataForModal}
        />
      )}
    </div>
  );
};

export default TopNavbar;
